import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { getDuels, getActiveDuels, getPublicDuels, getPublicActiveDuels } from '../api';
import DuelCard from '../components/duels/DuelCard';
import QuickDuel from '../components/duels/QuickDuel';
import HrnbLoader from '../components/controls/HrnbLoader';
import HrnbAlert from '../components/controls/HrnbAlert';
import HrnbButton from '../components/controls/HrnbButton';
import { HrnbTabs, HrnbTab } from '../components/controls/HrnbTabs';
import { PlusIcon, BackIcon, ForwardIcon, DuelIcon, LoginIcon } from '../icons';
import '../css/pages/DuelsPage.css';

/**
 * Страница списка дуэлей
 */
function DuelsPage() {
    const { currentUser } = useContext(UserContext);
    const [duels, setDuels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('all');
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const size = 10;

    // Загрузка дуэлей
    useEffect(() => {
        const loadDuels = async () => {
            setLoading(true);
            setError(null);

            try {
                let response;
                // Для неавторизованных пользователей используем публичный API
                if (!currentUser) {
                    if (activeTab === 'active') {
                        response = await getPublicActiveDuels(page, size);
                    } else {
                        response = await getPublicDuels(page, size);
                    }
                } else {
                    if (activeTab === 'active') {
                        response = await getActiveDuels(page, size);
                    } else {
                        response = await getDuels(page, size);
                    }
                }

                setDuels(response.content || []);
                setTotalPages(response.totalPages || 0);
                setTotalElements(response.totalElements || 0);
            } catch (err) {
                console.error('Ошибка при загрузке дуэлей:', err);
                setError('Не удалось загрузить дуэли. Попробуйте позже.');
            } finally {
                setLoading(false);
            }
        };

        loadDuels();
    }, [activeTab, page, currentUser]);

    // Обработчик переключения вкладок
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setPage(0);
    };

    // Обработчик пагинации
    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setPage(newPage);
        }
    };

    return (
        <div className="duels-page">
            <div className="duels-page__header">
                <h1 className="duels-page__title">Дуэли</h1>
                {currentUser && (
                    <Link to="/duels/create">
                        <HrnbButton variant="primary" icon={<PlusIcon />}>
                            Создать дуэль
                        </HrnbButton>
                    </Link>
                )}
            </div>

            {/* Быстрая дуэль */}
            <div className="duels-page__quick-duel">
                <QuickDuel />
            </div>

            <HrnbTabs 
                value={activeTab}
                onChange={handleTabChange}
            >
                <HrnbTab label="Все дуэли" value="all" />
                <HrnbTab label="Активные" value="active" />
            </HrnbTabs>

            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            {loading ? (
                <HrnbLoader />
            ) : duels.length === 0 ? (
                <div className="duels-page__empty">
                    <p>
                        {activeTab === 'active' 
                            ? 'Нет активных дуэлей' 
                            : 'Дуэлей пока нет. Будьте первым!'
                        }
                    </p>
                    {activeTab === 'all' && (
                        currentUser ? (
                            <Link to="/duels/create">
                                <HrnbButton variant="primary" icon={<DuelIcon />}>
                                    Создать первую дуэль
                                </HrnbButton>
                            </Link>
                        ) : (
                            <Link to="/login">
                                <HrnbButton variant="primary" icon={<LoginIcon />}>
                                    Войти для создания дуэли
                                </HrnbButton>
                            </Link>
                        )
                    )}
                </div>
            ) : (
                <>
                    <div className="duels-page__info">
                        Всего: {totalElements} дуэлей
                    </div>

                    <div className="duels-page__list">
                        {duels.map((duel) => (
                            <DuelCard key={duel.id} duel={duel} />
                        ))}
                    </div>

                    {totalPages > 1 && (
                        <div className="duels-page__pagination">
                            <HrnbButton
                                variant="secondary"
                                disabled={page === 0}
                                onClick={() => handlePageChange(page - 1)}
                                icon={<BackIcon />}
                            >
                                Назад
                            </HrnbButton>

                            <span className="duels-page__page-info">
                                Страница {page + 1} из {totalPages}
                            </span>

                            <HrnbButton
                                variant="secondary"
                                disabled={page >= totalPages - 1}
                                onClick={() => handlePageChange(page + 1)}
                                icon={<ForwardIcon />}
                            >
                                Вперёд
                            </HrnbButton>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default DuelsPage;
